import React, { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  TrendingUp,
  Plus,
  ChevronUp,
  ChevronDown,
  X,
  Users,
  Trophy,
} from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { AddPromoterDropdown } from "../fundraiser-builder/DropdownButton";
import { AddMultiplePromotersModal } from "../AddMultiplePromotersModal";
import { AddSinglePromoterModal } from "../AddSinglePromoterModal";
import { EditPromoterModal } from "../EditPromoterModal";
import { Contact } from "../ContactData";
import { Toast } from "../Toast";
import { SocialShareModal } from "../SocialShareModal";
import { PromoterActionsDropdown } from "../PromoterActionsDropdown";
import { RemovePromoterModal } from "../RemovePromoterModal";
import { FundraiserPromotersTabRewards } from "./FundraiserPromotersTabRewards";
import {
  SearchWrapper,
  SearchInput,
  ClearButton,
  DataTable,
  TableHeader,
  TableRow,
  NoResults,
  EmptyState,
  EmptyStateIcon,
} from "../shared/StyledComponents";
import styled from "styled-components";
import { FundRaiserStatus } from "../../enums";
import { ContactReferenceModel } from "@mindmemobile/mm-payment-modules";
import { CreateFundraiserModel, PromoterProgressModel } from "../../models";
import { AuthService, withRouter } from "@mindmemobile/mm-shared-global";
import { FundraiserService } from "../../services";
import { IAPICreateMultiplePromoterRequest, IFundPromoters } from "../..";
import { NavigateFunction } from "react-router-dom";
import { observer } from "mobx-react-lite";
import ToastService from "../ToastService";

const LeaderboardContainer = styled.div`
  background: linear-gradient(135deg, #f8fafc, #ffffff);
  border-radius: 20px;
  padding: 32px;
  margin-bottom: 32px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const LeaderboardTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LeaderboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const LeaderCard = styled.div<{ rank: number }>`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 20px;
  min-height: 100px;
  min-width: 280px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
`;

const LeaderAvatarContainer = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const LeaderAvatar = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const LeaderAvatarFallback = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000000;
  font-weight: 700;
  font-size: 18px;
  border: 3px solid #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const LeaderInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const LeaderName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LeaderStats = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`;

const LeaderAmount = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #10b981;
`;

const SectionHeaderWithSearch = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SortableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #10b981;
  }
`;

const SortIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0.6;
`;

const PromoterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const PromoterAvatarSmall = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const PromoterAvatarSmallFallback = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000000;
  font-weight: 700;
  font-size: 14px;
  border: 2px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const PromoterDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const PromoterName = styled.div`
  font-weight: 700;
  color: #1e293b;
  font-size: 16px;
  margin-bottom: 4px;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ContactItem = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
`;

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-start;
`;

const ProgressBar = styled.div`
  width: 120px;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ percentage: number }>`
  width: ${(props) => Math.min(props.percentage, 100)}%;
  height: 100%;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 4px;
  transition: width 0.8s ease;
`;

const ProgressText = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  white-space: nowrap;
`;

const TotalRaisedValue = styled.div`
  font-weight: 700;
  color: #1e293b;
  font-size: 16px;
  text-align: left;
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${(props) => {
    switch (props.status) {
      case "active":
        return "background: #dcfce7; color: #166534;";
      case "inactive":
        return "background: #fef3c7; color: #92400e;";
      default:
        return "background: #f3f4f6; color: #374151;";
    }
  }}
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding-left: 42px;
`;

const ActionsHeader = styled.div`
  padding-left: 42px;
`;

const Modal = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
  padding: 20px;
  box-sizing: border-box;
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 32px;
  width: 100%;
  max-width: 600px;
  max-height: 95vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: #e2e8f0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 52px;
  height: 28px;
  cursor: pointer;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: #10b981;
  }

  &:checked + span:before {
    transform: translateX(24px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: 0.3s;
  border-radius: 28px;

  &:before {
    position: absolute;
    content: "";
    height: 20px;
    width: 20px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

type SortField = "name" | "email" | "goal" | "progress" | "donations" | null;
type SortDirection = "asc" | "desc";

// Styled components for Achievements section
const AchievementsSection = styled.div`
  margin-top: 40px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
`;

interface FundraiserPromotersTabProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  navigate: NavigateFunction;
}

const FundraiserPromotersTab = observer(
  ({ searchTerm, onSearchChange, navigate }: FundraiserPromotersTabProps) => {
    const [showAddSingleModal, setShowAddSingleModal] = useState(false);
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [promoterToEdit, setPromoterToEdit] =
      useState<PromoterProgressModel | null>(null);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [promoterToRemove, setPromoterToRemove] =
      useState<PromoterProgressModel | null>(null);
    const [sortField, setSortField] = useState<SortField>("progress");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
    const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
    const [achievementImageErrors, setAchievementImageErrors] = useState<
      Set<string>
    >(new Set());

    const selectedFundraiser = fundraiserStore.selectedFundraiser;
    const canAddPromoters =
      selectedFundraiser &&
      ["Draft", "Active", "Inactive"].includes(selectedFundraiser.status);

    const promoters =
      fundraiserStore.fundraiserDetailsPageData.promoterGridData;
    const topPromoters = promoters
      ? promoters
        .sort((a, b) => b.totalRaisedAmount - a.totalRaisedAmount)
        .slice(0, 3)
      : [];

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    };

    const getInitials = (name: string) => {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase();
    };

    const handleImageError = (promoterId: string) => {
      setImageErrors((prev) => {
        const next = new Set(prev);
        next.add(promoterId);
        return next;
      });
    };

    const handleAchievementImageError = (promoterId: string) => {
      setAchievementImageErrors(
        (prev) => new Set(Array.from(prev).concat(promoterId))
      );
    };

    const renderLeaderAvatar = (promoter: PromoterProgressModel) => {
      if (imageErrors.has(promoter.id)) {
        return (
          <LeaderAvatarFallback>
            {getInitials(promoter.displayName)}
          </LeaderAvatarFallback>
        );
      }

      return (
        <LeaderAvatar
          src={promoter.imageURL}
          alt={promoter.displayName}
          onError={() => handleImageError(promoter.id)}
        />
      );
    };

    const handleSort = (field: SortField) => {
      if (sortField === field) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else {
        setSortField(field);
        setSortDirection(field === "donations" ? "desc" : "asc");
      }
    };

    const clearSearch = () => {
      onSearchChange("");
    };

    const handleAddPromoter = () => {
      setShowAddSingleModal(true);
    };

    const handleSelectContacts = (contacts: ContactReferenceModel[]) => {
      if (!selectedFundraiser) return;
      const contactIds = contacts.map((c) => c.id);
      var allContacts = fundraiserStore.contacts.filter(
        (contact) =>
          contactIds.includes(contact.id) ||
          promoters.some(
            (p) =>
              p.contactId === contact.id && !contactIds.includes(contact.id)
          )
      );

      // Check for existing promoters by email
      const existingContacts = new Set(
        promoters
          .filter((p) => p.contactId && p.fundraiserId === selectedFundraiser.id)
          .map((p) => p.contactId)
      );

      const newContacts = allContacts.filter(
        (contact) => !existingContacts.has(contact.id)
      ).map((contact) => {
        contact.isNew = true;
        return contact;
      });

      if (newContacts.length === 0) {
        ToastService.showWarning("All selected contacts are already promoters");
        return;
      }

      const request: IAPICreateMultiplePromoterRequest = {
        fundRaiserId: selectedFundraiser.id,
        accountId: AuthService.authData.accountId,
        interestIds: [],
        listIds: [],
        tagsIds: [],
        typeIds: [],
        promoterGoalAmount: fundraiserStore.getDefaultPromoterGoal(selectedFundraiser.id),
        promoters: allContacts.map((contact) => {
          return {
            contactId: contact.id,
            mindMeContactId: contact.mindmeContactId,
            goalAmount: fundraiserStore.getDefaultPromoterGoal(selectedFundraiser.id),
            isActive: true,
          } as IFundPromoters;
        }),
      };
      FundraiserService.addNewPromoters(request).subscribe({
        next: (response) => {
          if (response.result) {
            const newPromoters: PromoterProgressModel[] =
              CreateFundraiserModel.deserializePromoters(
                selectedFundraiser.id,
                fundraiserStore.getDefaultPromoterGoal(selectedFundraiser.id),
                promoters?.length,
                newContacts
              );

            fundraiserStore.setFundraiserDetailPageData(
              new CreateFundraiserModel({
                ...fundraiserStore.fundraiserDetailsPageData,
                promoterGridData: [...promoters, ...newPromoters],
              })
            );

            ToastService.showSuccess(
              `${newContacts.length} promoter${newContacts.length !== 1 ? "s" : ""} added successfully`
            );
            setShowAddSingleModal(false);
            setShowSelectModal(false);
            return;
          }
          ToastService.showError("Failed to add promoters");
        },
      });
    };

    const handleEditPromoter = (promoter: PromoterProgressModel) => {
      // Clear any existing toast when opening edit modal
      setPromoterToEdit(promoter);
      setShowEditModal(true);
    };

    const handleUpdatePromoter = (updatedPromoter: PromoterProgressModel) => {
      const updatedPromoters = CreateFundraiserModel.updatePromoter(
        promoters,
        updatedPromoter.id,
        updatedPromoter
      );
      fundraiserStore.setFundraiserDetailPageData(
        new CreateFundraiserModel({
          ...fundraiserStore.fundraiserDetailsPageData,
          promoterGridData: updatedPromoters,
        })
      );
    };

    const handleCloseEditModal = () => {
      setShowEditModal(false);
      setPromoterToEdit(null);
    };

    const handleMakeInactive = (isActive: boolean) => {
      if (promoterToRemove) {
        promoterToRemove.isActive = isActive === true;
        FundraiserService.updatePromoter(
          PromoterProgressModel.createAPIRequestModel(
            selectedFundraiser.id,
            promoterToRemove
          )
        ).subscribe({
          next: (response) => {
            if (response.result) {
              // Remove immediately without confirmation
              const newStatus = promoterToRemove.isActive === true;
              const promoterIndex =
                fundraiserStore.fundraiserDetailsPageData.promoterGridData.findIndex(
                  (p) => p.id === promoterToRemove.id
                );
              if (promoterIndex !== -1) {
                fundraiserStore.fundraiserDetailsPageData.promoterGridData[
                  promoterIndex
                ].isActive = newStatus;
              }
              setShowRemoveModal(false);
              setPromoterToRemove(null);
              ToastService.showSuccess(
                `Promoter made ${newStatus ? "active" : "inactive"} successfully`
              );
            }
          },
        });
      }
    };

    const handleCloseRemoveModal = () => {
      setShowRemoveModal(false);
      setPromoterToRemove(null);
    };

    /* What a promoter has raised, as shown in the Promoters tab. This summed the donations list by
       promoterId, but that list carries no promoter id, so it was always $0 and a promoter with
       donations was removed without the "make inactive instead" prompt. */
    const getPromoterRemovalTotal = (promoterId: string) =>
      promoters.find((p) => p.id === promoterId)?.totalRaisedAmount || 0;

    const removePromoterFromStore = (promoterId: string) => {
      // Remove from global promoters array
      const promoterIndex = fundraiserStore.promoters?.findIndex(
        (p) => p.id === promoterId
      );
      if (promoterIndex !== -1) {
        fundraiserStore.promoters?.splice(promoterIndex, 1);
        fundraiserStore.setPromoters(fundraiserStore.promoters);
      }
      const index =
        fundraiserStore.fundraiserDetailsPageData.promoterGridData?.findIndex(
          (p) => p.id === promoterId
        );
      if (index !== -1) {
        fundraiserStore.fundraiserDetailsPageData.promoterGridData?.splice(
          index,
          1
        );
        fundraiserStore.setFundraiserDetailPageData(
          fundraiserStore.fundraiserDetailsPageData
        );
      }
    };

    const baseURL  =fundraiserStore.getFundRaiserDomainBaseURL();
    const handlePreviewPromoter = (promoter: PromoterProgressModel) => {
      const fundraiserId = fundraiserStore.selectedFundraiser?.id || "";
      // Set the promoter context in the URL for proper routing
      // window.location.hash = `#fundraiser/${fundraiserId}?PID=${promoter.contactId}`; 
      window.open(`${baseURL}/fundraiser/${fundraiserId}?PID=${promoter.id}`, "_blank");
    };

    const handleInvitePromoter = (promoter: PromoterProgressModel) => {
      const fundraiserId = fundraiserStore.selectedFundraiser?.id || "";
      window.open(`${baseURL}/fundraiser/promoter-invite/${fundraiserId}?PID=${promoter.id}`, "_blank");
    };

    const handlePromoterDashboard = useCallback(
      (promoter: PromoterProgressModel) => {
        // Navigate to promoter dashboard page
        const fundraiserId = fundraiserStore.selectedFundraiser?.id || "";
        window.open(`${baseURL}/fundraiser/promoter-dashboard/${fundraiserId}?PID=${promoter.id}`, "_blank");
      },
      []
    );

    const handleCopyPromoterLink = async (promoter: PromoterProgressModel) => {
      const fundraiserId = fundraiserStore.selectedFundraiser?.id || "";
      const url = fundraiserStore.selectedFundraiser?.pageName
        ? fundraiserStore.getFundraiserPublicLinkByShortUniqueId(
          AuthService.shortUniqueId,
          fundraiserStore.selectedFundraiser.pageName,
          promoter.contactId
        )
        : fundraiserStore.getFundraiserPublicLink(
          fundraiserId,
          promoter.contactId
        );
      try {
        await navigator.clipboard.writeText(url);
        ToastService.showSuccess("Promoter link copied to clipboard");
      } catch (err) {
        const textArea = document.createElement("textarea");
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        ToastService.showSuccess("Promoter link copied to clipboard");
      }
    };

    const handleSharePromoter = (promoter: PromoterProgressModel) => {
      fundraiserStore.setSelectedPromoter(promoter.id);
      setShowShareModal(true);
    };

    const renderPromoterAvatar = (promoter: PromoterProgressModel) => {
      if (imageErrors.has(promoter.id)) {
        return (
          <PromoterAvatarSmallFallback>
            {getInitials(promoter.displayName)}
          </PromoterAvatarSmallFallback>
        );
      }

      return (
        <PromoterAvatarSmall
          src={promoter.imageURL}
          alt={promoter.displayName}
          onError={() => handleImageError(promoter.id)}
        />
      );
    };

    // Only show header if there are promoters
    const shouldShowHeader = promoters?.length > 0;

    const filteredPromoters = promoters
      ? promoters?.filter((promoter) => {
        if (!searchTerm) return true;

        const searchLower = searchTerm.toLowerCase();
        return (
          promoter.displayName.toLowerCase().includes(searchLower) ||
          promoter.email.toLowerCase().includes(searchLower) ||
          promoter.mobile.includes(searchLower)
        );
      })
      : [];

    const getSortedPromoters = () => {
      let sorted = [...filteredPromoters];

      // Default to sorting by donations (totalRaised) descending
      if (sortField === "donations" || sortField === null) {
        sorted = sorted.sort((a, b) => {
          const comparison = a.totalRaisedAmount - b.totalRaisedAmount;
          return sortDirection === "asc" ? comparison : -comparison;
        });
      } else if (sortField === "name") {
        sorted = sorted.sort((a, b) => {
          const lastNameA = a.displayName.split(" ").pop()?.toLowerCase() || "";
          const lastNameB = b.displayName.split(" ").pop()?.toLowerCase() || "";
          const comparison = lastNameA.localeCompare(lastNameB);
          return sortDirection === "asc" ? comparison : -comparison;
        });
      } else if (sortField === "progress") {
        sorted = sorted.sort((a, b) => {
          const progressA = (a.totalRaisedAmount / a.goalAmount) * 100;
          const progressB = (b.totalRaisedAmount / b.goalAmount) * 100;
          const comparison = progressA - progressB;
          return sortDirection === "asc" ? comparison : -comparison;
        });
      }
      return sorted;
    };

    const renderSortIcon = (field: SortField) => {
      if (sortField !== field) {
        return (
          <SortIcon>
            <ChevronUp size={12} />
            <ChevronDown size={12} style={{ marginTop: "-2px" }} />
          </SortIcon>
        );
      }

      return (
        <SortIcon style={{ opacity: 1 }}>
          {sortDirection === "asc" ? (
            <ChevronUp size={16} />
          ) : (
            <ChevronDown size={16} />
          )}
        </SortIcon>
      );
    };

    const sortedPromoters = getSortedPromoters();
    const showToastMessage = (message: string) => {
      ToastService.showSuccess(message);
    };

    const handleRemovePromoter = (promoter: PromoterProgressModel) => {
      if (!selectedFundraiser) return;

      // A promoter who has raised money cannot be removed; the modal offers "make inactive".
      if (getPromoterRemovalTotal(promoter.id) > 0 || (promoter.totalDonations || 0) > 0) {
        // Show confirmation modal
        setPromoterToRemove(promoter);
        setShowRemoveModal(true);
      } else {
        const contactIds = promoters
          .filter((p) => p.id !== promoter.id && p.contactId)
          .map((p) => p.contactId);
        FundraiserService.updatePromoters(
          selectedFundraiser.id,
          contactIds
        ).subscribe({
          next: (response) => {
            if (!response.result) {
              return;
            }
            // Remove immediately without confirmation
            removePromoterFromStore(promoter.id);
            setPromoterToRemove(null);
            ToastService.showSuccess("Promoter removed successfully");
          },
        });
      }
    };

    // Define dropdown options
    // Define dropdown options - match PromotersStep format
    const dropdownOptions = [
      {
        id: "multiple",
        title: "Add Multiple Promoters",
        description: "Select contacts by type, list, interest or tag",
        icon: <Users size={16} />,
        onClick: () => setShowSelectModal(true),
      },
      {
        id: "single",
        title: "Add Single Promoter",
        description: "Add new or existing contact<br />from your contact list",
        icon: <Plus size={16} />,
        onClick: handleAddPromoter,
      },
    ];

    return (
      <>
        {/* Top Promoters Leaderboard */}
        {topPromoters.length > 0 && (
          <LeaderboardContainer>
            <LeaderboardTitle>
              <TrendingUp size={24} />
              Top Promoters
            </LeaderboardTitle>
            <LeaderboardGrid>
              {topPromoters.map((promoter, index) => {
                const percentage =
                  promoter.goalAmount > 0
                    ? (promoter.totalRaisedAmount / promoter.goalAmount) * 100
                    : 0;
                return (
                  <LeaderCard key={promoter.id} rank={index + 1}>
                    <LeaderAvatarContainer>
                      {renderLeaderAvatar(promoter)}
                    </LeaderAvatarContainer>
                    <LeaderInfo>
                      <LeaderName>{promoter.displayName}</LeaderName>
                      <LeaderStats>
                        {percentage > 0 ? percentage.toFixed(1) : percentage}%
                        of goal
                      </LeaderStats>
                    </LeaderInfo>
                    <LeaderAmount>
                      {formatCurrency(promoter.totalRaisedAmount)}
                    </LeaderAmount>
                  </LeaderCard>
                );
              })}
            </LeaderboardGrid>
          </LeaderboardContainer>
        )}

        {/* Section Header with Search and Add Button - only show if there are promoters */}
        {shouldShowHeader && (
          <SectionHeaderWithSearch>
            <SectionTitle>Promoters ({filteredPromoters.length})</SectionTitle>
            <RightControls>
              {canAddPromoters && (
                <SearchWrapper>
                  <SearchInput
                    placeholder="Search promoters..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                  />
                  {searchTerm && (
                    <ClearButton onClick={clearSearch} title="Clear search">
                      <X size={16} />
                    </ClearButton>
                  )}
                </SearchWrapper>
              )}
              {canAddPromoters && (
                <AddPromoterDropdown options={dropdownOptions} />
              )}
              {!canAddPromoters && (
                <SearchWrapper>
                  <SearchInput
                    placeholder="Search promoters..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                  />
                  {searchTerm && (
                    <ClearButton onClick={clearSearch} title="Clear search">
                      <X size={16} />
                    </ClearButton>
                  )}
                </SearchWrapper>
              )}
            </RightControls>
          </SectionHeaderWithSearch>
        )}

        {!shouldShowHeader ? (
          <EmptyState>
            <EmptyStateIcon>
              <Users size={40} />
            </EmptyStateIcon>
            <h4
              style={{
                margin: "0 0 8px 0",
                fontSize: "22px",
                fontWeight: "700",
              }}
            >
              No Promoters Yet
            </h4>
            <br />
            <p style={{ margin: "0", lineHeight: "1.5" }}>
              Add promoters who will help spread the word and raise funds for
              this fundraiser.
              <br />
              Each promoter can have a custom fundraising goal.
              {!canAddPromoters && (
                <>
                  <br />
                  <br />
                  New promoters cannot be added if the fundraiser status is
                  completed.
                </>
              )}
            </p>
            {canAddPromoters ? (
              <div style={{ marginTop: "32px" }}>
                <AddPromoterDropdown options={dropdownOptions} />
              </div>
            ) : (
              <div style={{ marginTop: "32px" }}>
                <div
                  style={{
                    padding: "12px 24px",
                    background: "#f3f4f6",
                    color: "#9ca3af",
                    borderRadius: "8px",
                    fontWeight: "600",
                    opacity: 0.7,
                    cursor: "not-allowed",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Plus size={16} />
                  Add Promoters
                </div>
              </div>
            )}
          </EmptyState>
        ) : shouldShowHeader ? (
          <DataTable
            style={{ minHeight: sortedPromoters.length > 0 ? "500px" : "auto" }}
          >
            <TableHeader columns="1.5fr 1.1fr 0.4fr 0.2fr 110px 260px">
              <SortableHeader onClick={() => handleSort("name")}>
                <span>Name</span>
                {renderSortIcon("name")}
              </SortableHeader>

              <SortableHeader onClick={() => handleSort("progress")}>
                <span>Progress</span>
                {renderSortIcon("progress")}
              </SortableHeader>

              <SortableHeader
                onClick={() => handleSort("donations")}
                style={{ textAlign: "right" }}
              >
                <span>DONATIONS</span>
                {renderSortIcon("donations")}
              </SortableHeader>
              <div></div>
              <div>Status</div>
              <ActionsHeader>Actions</ActionsHeader>
            </TableHeader>

            {sortedPromoters.length === 0 ? (
              <NoResults>
                {searchTerm
                  ? `No promoters found matching "${searchTerm}"`
                  : "No promoters found"}
              </NoResults>
            ) : (
              sortedPromoters.map(
                (promoter: PromoterProgressModel, index: number) => {
                  let percentage =
                    promoter.goalAmount > 0
                      ? (promoter.totalRaisedAmount / promoter.goalAmount) * 100
                      : 0;

                  percentage = percentage > 100 ? 100 : percentage;
                  return (
                    <TableRow
                      key={promoter.id}
                      columns="1.5fr 1.1fr 0.4fr 0.2fr 110px 260px"
                    >
                      <PromoterInfo>
                        {renderPromoterAvatar(promoter)}
                        <PromoterDetails>
                          <PromoterName>{promoter.displayName}</PromoterName>
                          <ContactInfo>
                            <ContactItem>{promoter.mobile}</ContactItem>
                            <ContactItem>{promoter.email}</ContactItem>
                          </ContactInfo>
                        </PromoterDetails>
                      </PromoterInfo>

                      <ProgressContainer>
                        <ProgressBar>
                          <ProgressFill
                            percentage={Math.min(percentage || 0, 100)}
                          />
                        </ProgressBar>
                        <ProgressText>
                          {percentage > 0 ? percentage.toFixed(1) : percentage}%
                        </ProgressText>
                      </ProgressContainer>

                      <TotalRaisedValue style={{ textAlign: "right" }}>
                        {formatCurrency(promoter.totalRaisedAmount)}
                      </TotalRaisedValue>

                      <div></div>

                      <div style={{ textAlign: "left" }}>
                        <StatusBadge
                          status={promoter.isActive ? "active" : "inactive"}
                        >
                          {promoter.isActive ? "Active" : "Inactive"}
                        </StatusBadge>
                      </div>

                      <ActionsContainer>
                        <PromoterActionsDropdown
                          onDashboard={() => handlePromoterDashboard(promoter)}
                          onDonate={() => handlePreviewPromoter(promoter)}
                          onInvite={() => handleInvitePromoter(promoter)}
                          onEdit={() => handleEditPromoter(promoter)}
                          onCopyLink={() => handleCopyPromoterLink(promoter)}
                          onShare={() => handleSharePromoter(promoter)}
                          onDelete={() => handleRemovePromoter(promoter)}
                        />
                      </ActionsContainer>
                    </TableRow>
                  );
                }
              )
            )}
          </DataTable>
        ) : null}

        {/* Add Single Promoter Modal */}
        <AddSinglePromoterModal
          promoters={promoters}
          fromDetailPage={true}
          isVisible={showAddSingleModal}
          fundraiserId={selectedFundraiser?.id || ""}
          onClose={() => setShowAddSingleModal(false)}
          updatePromoters={(updatedPromoters) =>
            fundraiserStore.setFundraiserDetailPageData(
              new CreateFundraiserModel({
                ...fundraiserStore.fundraiserDetailsPageData,
                promoterGridData: [...updatedPromoters],
              })
            )
          }
          onSelect={handleSelectContacts}
        />

        {/* Edit Promoter Modal */}
        <EditPromoterModal
          isVisible={showEditModal}
          onClose={handleCloseEditModal}
          promoter={promoterToEdit}
          onUpdate={handleUpdatePromoter}
        />

        {showShareModal &&
          createPortal(
            <SocialShareModal
              gallerySettings={fundraiserStore.gallerySettings}
              isVisible={showShareModal}
              onClose={() => setShowShareModal(false)}
              shortUniqueId={AuthService.shortUniqueId}
            />,
            document.body
          )}

        {/* Remove Promoter Modal */}
        <RemovePromoterModal
          isVisible={showRemoveModal}
          promoter={promoterToRemove}
          onClose={handleCloseRemoveModal}
          onMakeInactive={handleMakeInactive}
          formatCurrency={formatCurrency}
          getRemovalTotal={getPromoterRemovalTotal}
        />

        {/* Select Multiple Promoters Modal */}
        <AddMultiplePromotersModal
          visible={showSelectModal}
          fromDetailPage={true}
          promoters={promoters}
          onClose={() => setShowSelectModal(false)}
          onSelect={handleSelectContacts}
          fundraiserId={selectedFundraiser?.id || ""}
        />
        {/* Rewards Section */}
        <FundraiserPromotersTabRewards promoters={promoters} />
      </>
    );
  }
);
export default withRouter(FundraiserPromotersTab);
